import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { PickupPointAdmin } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const ghostBtn =
  'rounded-md border border-admin-border bg-white px-4 py-2 text-sm hover:bg-admin-cream disabled:opacity-50';
const dangerBtn =
  'rounded-md border border-admin-danger bg-white px-3 py-1.5 text-sm text-admin-danger hover:bg-admin-cream disabled:opacity-50';

interface FormState {
  name: string;
  address: string;
  daysToCollect: number;
  isActive: boolean;
  sortOrder: number;
}

const initialForm: FormState = {
  name: '',
  address: '',
  daysToCollect: 3,
  isActive: true,
  sortOrder: 0,
};

export default function AdminPickupPointFormPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<FormState>(initialForm);
  const [imageThumbUrl, setImageThumbUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const response = await api.get<PickupPointAdmin>(`/admin/pickup-points/${id}`);
        const p = response.data;
        setForm({
          name: p.name,
          address: p.address,
          daysToCollect: p.daysToCollect,
          isActive: p.isActive,
          sortOrder: p.sortOrder,
        });
        setImageThumbUrl(p.imageThumbUrl);
      } catch (err) {
        toast.error(extractError(err, 'Falha ao carregar ponto de recolha'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const uploadImage = async (pointId: string, file: File): Promise<void> => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post<PickupPointAdmin>(
        `/admin/pickup-points/${pointId}/image`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImageThumbUrl(res.data.imageThumbUrl);
    } catch (err) {
      toast.error(extractError(err, 'Falha ao enviar imagem'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = async (): Promise<void> => {
    if (!id) return;
    if (!window.confirm('Remover a foto atual?')) return;
    setUploading(true);
    try {
      await api.delete(`/admin/pickup-points/${id}/image`);
      setImageThumbUrl(null);
    } catch (err) {
      toast.error(extractError(err, 'Falha ao remover imagem'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit && id) {
        await api.patch(`/admin/pickup-points/${id}`, form);
        const pending = fileInputRef.current?.files?.[0];
        if (pending) await uploadImage(id, pending);
        toast.success('Ponto de recolha guardado.');
      } else {
        const created = await api.post<PickupPointAdmin>('/admin/pickup-points', form);
        const newId = created.data.id;
        const pending = fileInputRef.current?.files?.[0];
        if (pending) await uploadImage(newId, pending);
        toast.success('Ponto de recolha criado.');
        navigate(`/admin/pontos-de-recolha/${newId}`);
      }
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar ponto de recolha'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-admin-text-soft">A carregar…</p>;

  return (
    <>
      <SEO title={isEdit ? 'Editar ponto de recolha' : 'Novo ponto de recolha'} noindex />
      <div className="mb-2 text-sm">
        <Link to="/admin/pontos-de-recolha" className="text-admin-primary hover:underline">
          ← Pontos de recolha
        </Link>
      </div>
      <h1>{isEdit ? 'Editar ponto de recolha' : 'Novo ponto de recolha'}</h1>

      <form onSubmit={handleSubmit} className="mt-6 max-w-2xl space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Nome*</span>
          <input
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            required
            maxLength={150}
            className={`${inputClass} w-full`}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Morada*</span>
          <textarea
            value={form.address}
            onChange={(e) => setField('address', e.target.value)}
            required
            rows={3}
            className={`${inputClass} w-full`}
          />
          <span className="mt-1 block text-xs text-admin-muted">
            Texto livre. Será mostrado ao cliente no checkout.
          </span>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Dias para levantar*</span>
            <input
              type="number"
              min={1}
              max={60}
              value={form.daysToCollect}
              onChange={(e) => setField('daysToCollect', Number(e.target.value))}
              required
              className={`${inputClass} w-full`}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Ordem</span>
            <input
              type="number"
              value={form.sortOrder}
              onChange={(e) => setField('sortOrder', Number(e.target.value))}
              className={`${inputClass} w-full`}
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setField('isActive', e.target.checked)}
          />
          <span>Ativo (visível no checkout)</span>
        </label>

        <div>
          <span className="mb-1 block text-sm font-medium">Foto</span>
          {imageThumbUrl ? (
            <div className="mb-2">
              <img
                src={imageThumbUrl}
                alt="Foto atual"
                style={{ maxHeight: 180, maxWidth: 320, borderRadius: 4 }}
              />
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              disabled={uploading}
              className="text-sm"
              onChange={(e) => {
                if (!isEdit || !id) return;
                const file = e.target.files?.[0];
                if (file) void uploadImage(id, file);
              }}
            />
            {imageThumbUrl ? (
              <button
                type="button"
                className={dangerBtn}
                disabled={uploading}
                onClick={() => void removeImage()}
              >
                Remover foto
              </button>
            ) : null}
          </div>
          {!isEdit ? (
            <p className="mt-1 text-xs text-admin-muted">
              A foto será enviada após guardar o ponto pela primeira vez.
            </p>
          ) : null}
          {uploading ? (
            <p className="mt-1 text-xs text-admin-muted">A processar…</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button type="submit" disabled={saving || uploading} className={primaryBtn}>
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
          <button
            type="button"
            className={ghostBtn}
            onClick={() => navigate('/admin/pontos-de-recolha')}
            disabled={saving || uploading}
          >
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}
